import { Calendar, Mail, TreePalm } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { CALENDAR_FILTER_CATEGORY_COLORS, resolveCalendarFilterCategory } from '../types';
import type { CalendarEvent, CalendarFilterCategory } from '../types';

interface CalendarEventCardProps {
  event: CalendarEvent;
  onClick?: () => void;
}

// Mapa de colores de fondo con opacidad para el indicador lateral
const EVENT_TYPE_STRIP: Record<string, string> = {
  'bg-blue-500': 'bg-blue-300',
  'bg-emerald-500': 'bg-emerald-300',
  'bg-red-500': 'bg-red-300',
  'bg-violet-500': 'bg-violet-300',
  'bg-amber-500': 'bg-amber-300',
};

// Ícono distintivo por categoría (sin assets de marca — lucide genérico)
const CATEGORY_ICONS: Partial<Record<CalendarFilterCategory, typeof Calendar>> = {
  google: Calendar,
  microsoft: Mail,
  native: TreePalm,
};

/** `source: 'khronox'` events (ej. ausencias) carry their own status in `metadata.status` — overrides the fixed category color so approved/pending read distinctly. */
const KHRONOX_STATUS_COLORS: Record<string, string> = {
  Approved: 'bg-emerald-500',
  Pending: 'bg-amber-500',
};

export function CalendarEventCard({ event, onClick }: CalendarEventCardProps) {
  const category = resolveCalendarFilterCategory(event);
  const statusOverride =
    event.source === 'khronox'
      ? KHRONOX_STATUS_COLORS[String(event.metadata?.status ?? '')]
      : undefined;
  const bgColor = statusOverride ?? CALENDAR_FILTER_CATEGORY_COLORS[category];
  const CategoryIcon =
    category !== 'task' && category !== 'internal' ? CATEGORY_ICONS[category] : undefined;

  return (
    <div
      className={cn(
        'flex h-full min-h-[20px] w-full items-center overflow-hidden rounded-[var(--radius-md)] shadow-xs',
        bgColor,
        onClick && 'cursor-pointer',
      )}
      data-testid="calendar-event-card"
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
    >
      {/* Franja lateral más clara para dar profundidad */}
      <span
        className={cn('h-full w-1 flex-shrink-0', EVENT_TYPE_STRIP[bgColor] ?? 'bg-white/30')}
      />
      {CategoryIcon && (
        <CategoryIcon
          className="ml-1 h-3 w-3 flex-shrink-0 text-white/80"
          data-testid={`calendar-event-source-icon-${event.source}`}
          aria-hidden="true"
        />
      )}
      <span className="truncate px-1.5 py-0.5 text-xs font-medium leading-tight text-white">
        {event.title}
      </span>
    </div>
  );
}
